<template>
	<!-- Delivery estimate -->
	<v-page-product-availability-button icon="box" actionless>
		<!-- Locale-based text, skip in SSR -->
		<template v-if="isMounted">
			{{ deliveryEstimate }}
		</template>

		<v-skeleton-text v-else class="w-64" />
	</v-page-product-availability-button>

	<!-- Shipping price -->
	<v-page-product-availability-button
		icon="shipping"
		@click="isDeliveryOpen = true"
	>
		<!-- The info below is partially locale-based and also arrives late, so skip in SSR -->
		<v-skeleton-text v-if="!isMounted" class="w-64" />

		<!-- Not sure if this is smart, cheapestDeliveryPrice may not be initialized right away -->
		<template v-else-if="cheapestDeliveryPrice === undefined">
			{{ t("free-pickup") }}
		</template>

		<template v-else>
			<template v-if="promotionInfo?.deliveryPricingInfoOverride">
				{{ promotionInfo.deliveryPricingInfoOverride }}
			</template>

			<template v-else>
				{{
					t("product-delivery-with-pickup", {
						amount: formatCurrency(cheapestDeliveryPrice),
					})
				}}
			</template>
		</template>
	</v-page-product-availability-button>

	<!-- Outlets stock status -->
	<!-- The weird if condition is probably just a lazy way to disable this for
    estonia, since Estonia stores don't really have their own warehouses -->
	<v-page-product-availability-button
		v-if="outletGroupingAttributeId"
		icon="shop"
		@click="openAvailabilityModal"
	>
		<!-- Locale-based text, skip in SSR -->
		<template v-if="isMounted">
			{{
				actualOutletsCount >= 20
					? t("actual-availability", {
							actual: actualOutletsCount,
							total: outlets.length,
						})
					: modelOutletsCount >= 6
						? t("product-availability", {
								amount: modelOutletsCount,
								total: outlets.length,
							})
						: t("no-availability")
			}}
		</template>

		<v-skeleton-text v-else class="w-64" />
	</v-page-product-availability-button>

	<!-- <v-page-product-availability-button icon="location"
        disabled
        @click="isPostcodeOpen = true">
        Alueesi saatavuus- ja toimitustiedot
    </v-page-product-availability-button> -->

	<v-page-product-delivery-modal
		v-model="postalCode"
		:is-open="isDeliveryOpen"
		:groups="shippingMethodGroups"
		:is-free-shipping="isEligibleForFreeShipping"
		@close="isDeliveryOpen = false"
	/>

	<v-page-product-availability-modal
		v-if="outletGroupingAttributeId"
		:is-buyable="properties.product.inventory.isAvailableForPurchase"
		:is-open="isAvailabilityOpen"
		:outlets="outlets"
		@close="isAvailabilityOpen = false"
	/>

	<!-- <v-page-product-availability-postcode :is-open="isPostcodeOpen"
        @close="isPostcodeOpen = false"></v-page-product-availability-postcode> -->
</template>

<script lang="ts" setup>
import {useQuery} from "@pkg/shared/composables/use-query";
import {uniqBy} from "es-toolkit";
import {useGtm} from "@gtm-support/vue-gtm";
import {useCurrency} from "@pkg/shared/composables/use-currency";
import {useDeliveryEstimateText} from "@pkg/shared/composables/use-delivery-estimate-text";
import {useLifecycle} from "@pkg/shared/composables/use-lifecycle";
import {useOutletStocks} from "@pkg/shared/composables/use-outlet-stocks";
import {
	ShippingMethodType,
	type SimpleProductDataFragment,
	type ViewProductDetailsQuery,
	ViewProductShippingMethodsDocument,
	type ViewProductShippingMethodsQuery,
	type ViewProductShippingMethodsQueryVariables,
} from "@pkg/shared/graphql";

type Product = Extract<
	ViewProductShippingMethodsQuery["product"],
	{__typename: "SimpleProduct"}
>;
type ProductDetails = Extract<
	ViewProductDetailsQuery["product"],
	{__typename: "SimpleProduct"}
>;

type ShippingMethod = Product["availableShippingMethods"][0];
type ShippingMethodGroup = NonNullable<ShippingMethod["group"]>;
type ShippingMethodGroupCombined = ShippingMethodGroup & {
	children: ShippingMethod[];
};

interface PromotionInfo {
	deliveryPricingInfoOverride: string;
}

const properties = defineProps<{
	product: SimpleProductDataFragment;
	productDetails?: ProductDetails;
	promotionInfo?: PromotionInfo;
}>();

const {t} = useI18n();
const {format: formatCurrency} = useCurrency();
const {format: formatDeliveryEstimateText} = useDeliveryEstimateText();

const {isMounted} = useLifecycle();

const deliveryEstimate = computed(() =>
	formatDeliveryEstimateText(
		{
			inventory: properties.product.inventory,
			productClass: properties.product?.productClass?.title,
		},
		"product",
	),
);

const isDeliveryOpen = ref(false);
const isAvailabilityOpen = ref(false);
const postalCode = ref("");

const details = computed(() => properties.productDetails);

function postalCodeIsValid(value: string) {
	return /^\d{5}$/.test(value);
}

const {data: shippingData} = useQuery<
	ViewProductShippingMethodsQuery,
	ViewProductShippingMethodsQueryVariables
>({
	query: ViewProductShippingMethodsDocument,
	variables: computed(() => ({
		id: properties.product.id,
		postalCode: postalCodeIsValid(postalCode.value)
			? postalCode.value
			: t("postal-code-to-query"),
	})),
});

const isEligibleForFreeShipping = computed(() =>
	shippingData.value?.product.__typename === "SimpleProduct"
		? shippingData.value.product.isEligibleForFreeShipping
		: false,
);

const shippingMethods = computed(() =>
	shippingData.value?.product.__typename === "SimpleProduct"
		? shippingData.value.product.availableShippingMethods
		: [],
);

const shippingMethodGroups = computed<ShippingMethodGroupCombined[]>(() =>
	uniqBy(
		shippingMethods.value
			.map((method) => method.group)
			.filter((group): group is ShippingMethodGroup => !!group),
		(group) => group.id,
	).map((group: ShippingMethodGroup) => ({
		...group,
		children: shippingMethods.value.filter(
			(method) => method.group?.id === group.id,
		),
	})),
);

// Get the cheapest delivery price from the shipping methods.
// See '[product].vue'
const cheapestDeliveryPrice = computed<number | undefined>(() => {
	const prices = shippingMethods.value
		.filter(
			(method) => ![ShippingMethodType.PickUpFromStore].includes(method.type),
		)
		.map((method) => {
			if (
				isEligibleForFreeShipping.value &&
				method.freeShippingPriceWithServices
			) {
				return Number(method.freeShippingPriceWithServices.amount);
			}

			return Number(method.originalPriceWithServices.amount);
		});

	return prices.length > 0 ? Math.min(...prices) : undefined;
});

const {
	public: {outletGroupingAttributeId},
} = useRuntimeConfig();

const {outlets} = useOutletStocks(details);

const modelOutletsCount = computed(
	() => outlets.value.filter((outlet) => outlet.modelQuantity > 0).length,
);
const actualOutletsCount = computed(
	() => outlets.value.filter((outlet) => outlet.availableQuantity > 0).length,
);

const gtm = useGtm();

function openAvailabilityModal() {
	// Show availability modal and track event.
	isAvailabilityOpen.value = !isAvailabilityOpen.value;
	gtm?.trackEvent({
		event: "check_availability",
		_clear: true,
	});
}
</script>

<i18n lang="json">
{
	"et": {
		"product-delivery-with-pickup": "Kohaletoimetamine alates {amount} - Järeltulemine on alati tasuta!",
		"actual-availability": "Kohe kaasa {actual}/{total} kauplusest",
		"product-availability": "Toode kohapeal {amount}/{total} kaupluses",
		"no-availability": "Kontrolli kaupluste saadavust",
		"free-pickup": "Järeltulemine on alati tasuta!",
		"postal-code-to-query": "10001"
	},
	"fi": {
		"product-delivery-with-pickup": "Toimitukset noutopisteisiin 0 €, kotiintoimitukset alkaen {amount}",
		"actual-availability": "Heti mukaan {actual}/{total} myymälästä",
		"product-availability": "Esillä {amount}/{total} myymälässä",
		"no-availability": "Tarkista myymäläsaatavuus",
		"free-pickup": "Nouto on aina ilmainen!",
		"postal-code-to-query": "00100"
	}
}
</i18n>
